import React from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Container, Box, Typography, Paper, List, ListItem, Chip, Grid } from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import { translations, Translation } from './translations';

import Header from './components/Header';
import { DiJavascript1, DiReact, DiPostgresql, DiGit, DiDatabase, DiGithubBadge } from 'react-icons/di';
import { 
  SiRedux, 
  SiWebpack, 
  SiRabbitmq, 
  SiKubernetes, 
  SiGrafana, 
  SiKibana, 
  SiDocker, 
  SiFastlane, 
  SiExpress, 
  SiRedis, 
  SiXcode,
  SiNodedotjs,
  SiNginx,
  SiGithubactions
} from 'react-icons/si';
import { FaHtml5, FaCss3Alt } from 'react-icons/fa';
import { VscTerminalBash } from 'react-icons/vsc';
import ArchitectureIcon from '@mui/icons-material/Architecture';
import parse from 'html-react-parser';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import Fade from '@mui/material/Fade';
import ParticlesBackground from './components/ParticlesBackground';
import CodeIcon from '@mui/icons-material/Code';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import SettingsIcon from '@mui/icons-material/Settings';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';

// Обновляем анимацию бордера (более медленную и только с розово-фиолетовыми оттенками)
const borderAnimation = keyframes`
  0% { border-color: #ff69b4; }
  50% { border-color: #9400d3; }
  100% { border-color: #ff69b4; }
`;

// Затем создаем тему
const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#3b82f6',
      light: '#60a5fa',
      dark: '#2563eb',
    },
    secondary: {
      main: '#06b6d4',
      light: '#22d3ee',
      dark: '#0891b2',
    },
    background: {
      default: 'transparent',
      paper: 'rgba(255, 255, 255, 0.02)',
    },
    text: {
      primary: '#fff',
      secondary: 'rgba(255, 255, 255, 0.7)',
    },
  },
  typography: {
    h3: {
      fontWeight: 800,
      letterSpacing: '-0.5px',
      fontSize: '2.5rem',
      padding: '0.5rem',
      backgroundImage: 'linear-gradient(45deg,red,#a34dff 50%, #ff9fe1, purple,orange)',
      WebkitBackgroundClip: 'text',
      backgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      textFillColor: 'transparent',
      filter: 'drop-shadow(0 2px 2px rgba(0, 0, 0, 0.2))',
      '@media (max-width:900px)': {
        fontSize: '2.3rem'
      },
      '@media (max-width:600px)': {
        fontSize: '2rem'
      }
    },
    h5: {
      fontWeight: 600,
      letterSpacing: '0.5px',
      color: '#fff',
      opacity: 0.9,
      fontSize: '1.5rem',
      '@media (max-width:900px)': {
        fontSize: '1.3rem',
      },
      '@media (max-width:600px)': {
        fontSize: '1.1rem',
      },
    },
    h6: {
      fontWeight: 800,
      letterSpacing: '1px',
      textTransform: 'uppercase',
      fontSize: '1.25rem',
      color: '#3b82f6',
      marginBottom: '1.5rem',
      '@media (max-width:900px)': {
        fontSize: '1.1rem',
        marginBottom: '1.25rem'
      },
      '@media (max-width:600px)': {
        fontSize: '1rem',
        marginBottom: '1rem'
      }
    },
    body1: {
    },
    body2: {
      fontFamily: '"Gill Sans", "Gill Sans MT", "Calibri", sans-serif',
    },
    fontFamily: '"Gill Sans", "Gill Sans MT", "Calibri", sans-serif',
  },
  components: {
    MuiTypography: {
      styleOverrides: {
        root: {
          fontFamily: '"Gill Sans", "Gill Sans MT", "Calibri", sans-serif',
        },
      },
    },
    MuiSvgIcon: {
      styleOverrides: {
        root: {
          fontFamily: 'inherit',
        },
      },
    },
  },
});

const GlowingBackground = styled(Box)({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: 'linear-gradient(180deg, hsl(224, 57%, 18%), rgb(0, 34, 49), black)',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '0',
    left: '0',
    right: '0',
    bottom: '0',
    background: `
      radial-gradient(circle at 20% 20%, rgba(29, 78, 216, 0.15) 0%, transparent 40%),
      radial-gradient(circle at 80% 80%, rgba(0, 108, 156, 0.15) 0%, transparent 40%)
    `,
    filter: 'blur(80px)',
  },
  zIndex: -1,
});

interface ContentCardProps {
  noEffects?: boolean; // Prop для отключения эффектов
}

const ContentCard = styled(Paper, {
  shouldForwardProp: prop => prop !== 'noEffects'
})<ContentCardProps>(({ theme, noEffects }) => ({
  borderRadius: 32,
  padding: theme.spacing(4),
  overflow: 'hidden',
  position: 'relative',
  '@media (max-width:900px)': {
    borderRadius: 28,
    padding: theme.spacing(3)
  },
  '@media (max-width:600px)': {
    borderRadius: 24,
    padding: theme.spacing(2)
  },
  ...(noEffects ? {
    background: 'transparent',
    border: 'none',
    backdropFilter: 'none',
    boxShadow: 'none',
    '&::before': {
      display: 'none',
    },
  } : {
    background: 'rgba(255, 255, 255, 0.03)',
    backdropFilter: 'blur(20px)',
    border: '1px solid rgba(255, 255, 255, 0.05)',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: '1px',
      background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.1), transparent)',
    },
  }),
}));

// Обновляем стили для ProfileImage
const ProfileImage = styled('img')({
  width: '220px',
  height: '220px',
  '@media (max-width:900px)': {
    width: '180px',
    height: '180px',
  },
  '@media (max-width:600px)': {
    width: '160px',
    height: '160px',
  },
  borderRadius: '50%',
  objectFit: 'cover',
  border: '4px solid #ff69b4',
  padding: '4px',
  background: 'rgba(255, 255, 255, 0.02)',
  animation: `${borderAnimation} 3s infinite ease-in-out`,
});

const ExperienceCard = styled(Box)(({ theme }) => ({
  background: 'rgba(59, 130, 246, 0.05)',
  borderRadius: 24,
  padding: theme.spacing(3),
  border: '1px solid rgba(59, 130, 246, 0.1)',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'all 0.3s ease'
}));

const TelegramIcon = () => (
  <svg 
    width="24" 
    height="24" 
    viewBox="0 0 24 24" 
    fill="currentColor"
    style={{ marginRight: window.innerWidth > 600 ? '8px' : '0' }}
  >
    <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
  </svg>
);

const LineIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 448 512"
    fill="currentColor"
    style={{ marginRight: window.innerWidth > 600 ? '8px' : '0' }}
  >
    <path d="M272.1 204.2v71.1c0 1.8-1.4 3.2-3.2 3.2h-11.4c-1.1 0-2.1-.6-2.6-1.3l-32.6-44v42.2c0 1.8-1.4 3.2-3.2 3.2h-11.4c-1.8 0-3.2-1.4-3.2-3.2v-71.1c0-1.8 1.4-3.2 3.2-3.2H219c1 0 2.1.5 2.6 1.4l32.6 44v-42.2c0-1.8 1.4-3.2 3.2-3.2h11.4c1.8-.1 3.3 1.4 3.3 3.1zm-82-3.2h-11.4c-1.8 0-3.2 1.4-3.2 3.2v71.1c0 1.8 1.4 3.2 3.2 3.2h11.4c1.8 0 3.2-1.4 3.2-3.2v-71.1c0-1.7-1.4-3.2-3.2-3.2zm-27.5 59.6h-31.1v-56.4c0-1.8-1.4-3.2-3.2-3.2h-11.4c-1.8 0-3.2 1.4-3.2 3.2v71.1c0 .9.3 1.6.9 2.2.6.5 1.3.9 2.2.9h45.7c1.8 0 3.2-1.4 3.2-3.2v-11.4c0-1.7-1.4-3.2-3.1-3.2zM332.1 201h-45.7c-1.7 0-3.2 1.4-3.2 3.2v71.1c0 1.7 1.4 3.2 3.2 3.2h45.7c1.8 0 3.2-1.4 3.2-3.2v-11.4c0-1.8-1.4-3.2-3.2-3.2H301v-12h31.1c1.8 0 3.2-1.4 3.2-3.2V234c0-1.8-1.4-3.2-3.2-3.2H301v-12h31.1c1.8 0 3.2-1.4 3.2-3.2v-11.4c-.1-1.7-1.5-3.2-3.2-3.2zM448 113.7V399c-.1 44.8-36.8 81.1-81.7 81H81c-44.8-.1-81.1-36.9-81-81.7V113c.1-44.8 36.9-81.1 81.7-81H367c44.8.1 81.1 36.8 81 81.7zm-61.6 122.6c0-73-73.2-132.4-163.1-132.4-89.9 0-163.1 59.4-163.1 132.4 0 65.4 58 120.2 136.4 130.6 19.1 4.1 16.9 11.1 12.6 36.8-.7 4.1-3.3 16.1 14.1 8.8 17.4-7.3 93.9-55.3 128.2-94.7 23.6-26 34.9-52.3 34.9-81.5z"/>
  </svg>
);

const LinkedInIcon = () => (
  <svg 
    width="24" 
    height="24" 
    viewBox="0 0 24 24" 
    fill="currentColor"
    style={{ marginRight: window.innerWidth > 600 ? '8px' : '0' }}
  >
    <path d="M19 3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14m-.5 15.5v-5.3a3.26 3.26 0 0 0-3.26-3.26c-.85 0-1.84.52-2.32 1.3v-1.11h-2.79v8.37h2.79v-4.93c0-.77.62-1.4 1.39-1.4a1.4 1.4 0 0 1 1.4 1.4v4.93h2.79M6.88 8.56a1.68 1.68 0 0 0 1.68-1.68c0-.93-.75-1.69-1.68-1.69a1.69 1.69 0 0 0-1.69 1.69c0 .93.76 1.68 1.69 1.68m1.39 9.94v-8.37H5.5v8.37h2.77z"/>
  </svg>
);

const GmailIcon = () => (
  <svg 
    width="24" 
    height="24" 
    viewBox="0 0 24 24" 
    fill="currentColor"
    style={{ marginRight: window.innerWidth > 600 ? '8px' : '0' }}
  >
    <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
  </svg>
);

const SocialButton = styled('a')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  padding: '12px 24px',
  borderRadius: '12px',
  border: '1px solid transparent',
  backgroundImage: 'linear-gradient(rgba(255, 255, 255, 0.03), rgba(255, 255, 255, 0.03))',
  backgroundClip: 'padding-box',
  position: 'relative',
  color: '#fff',
  textDecoration: 'none',
  transition: 'all 0.3s ease',
  backdropFilter: 'blur(10px)',
  '& svg': {
    transition: 'transform 0.3s ease',
  },
  // Hover эффекты только для десктопа
  [theme.breakpoints.up('sm')]: {
    '&:hover': {
      background: 'rgba(59, 130, 246, 0.2)',
      boxShadow: '0 5px 15px rgba(59, 130, 246, 0.2)',
      '& svg': {
        transform: 'scale(1.1)',
      }
    }
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    borderRadius: '12px',
    padding: '1.5px',
    background: 'linear-gradient(45deg, rgba(255, 105, 180, 0.5), rgba(148, 0, 211, 0.5))',
    mask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
    maskComposite: 'exclude',
    WebkitMaskComposite: 'xor',
    pointerEvents: 'none'
  },
  // Мобильные стили
  [theme.breakpoints.down('sm')]: {
    padding: '12px',
    minWidth: '50px',
    height: '50px',
    justifyContent: 'center',
    '& svg': {
      marginRight: 0,
      width: '26px',
      height: '26px'
    },
    '& span': {
      display: 'none'
    }
  }
}));

const TechStackScroll = styled('div')({
  display: 'flex',
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  position: 'relative',
  '&::before, &::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    width: '80px',
    height: '100%',
    zIndex: 1,
    pointerEvents: 'none',
  },
  '&::before': {
    left: 0,
    background: 'linear-gradient(to right, rgba(13, 23, 41, 0.5), transparent)',
  },
  '&::after': {
    right: 0,
    background: 'linear-gradient(to left, rgba(13, 23, 41, 0.5), transparent)',
  },
});

const scrollAnimation = keyframes`
  0% { transform: translateX(0); }
  100% { transform: translateX(-50%); }
`;

const TechStackTrack = styled('div')({
  display: 'flex',
  animation: `${scrollAnimation} 30s linear infinite`,
  '&:hover': {
    animationPlayState: 'paused',
  },
});

const TechItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '12px 24px',
  background: 'rgba(59, 130, 246, 0.1)',
  borderRadius: '16px',
  border: '1px solid transparent',
  backgroundImage: 'linear-gradient(rgba(255, 255, 255, 0.03), rgba(255, 255, 255, 0.03))',
  backgroundClip: 'padding-box',
  position: 'relative',
  margin: '0 8px',
  whiteSpace: 'nowrap',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    borderRadius: '16px',
    padding: '1.5px',
    background: 'linear-gradient(45deg, rgba(255, 105, 180, 0.5), rgba(148, 0, 211, 0.5))',
    mask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
    maskComposite: 'exclude',
    WebkitMaskComposite: 'xor',
    pointerEvents: 'none'
  },
  [theme.breakpoints.down('sm')]: {
    padding: '8px 16px',
    margin: '0 6px',
    borderRadius: '12px',
    gap: '6px',
  }
}));

const techIconStyle = {
  fontSize: { xs: '1.2rem', sm: '1.5rem' },
  display: 'flex',
  alignItems: 'center'
};

// Обновляем стили для текста в карусели (убираем градиент)
const techTextStyle = {
  fontSize: { xs: '0.85rem', sm: '0.95rem' },
  fontWeight: 600,
  color: '#fff'
};

const commonTextStyles = {
  opacity: 0.9,
  fontSize: '1.1rem',
  lineHeight: 1.6,
  letterSpacing: '0.2px',
  color: 'rgba(255, 255, 255, 0.9)',
  fontFamily: '"Gill Sans", "Gill Sans MT", "Calibri", sans-serif',
  '@media (max-width:900px)': {
    fontSize: '1rem',
    lineHeight: 1.5
  },
  '@media (max-width:600px)': {
    fontSize: '0.9rem',
    lineHeight: 1.4
  }
};

const sectionTitleStyles = {
  mb: 2,
  fontWeight: 600,
  fontSize: { xs: '1.1rem', sm: '1.2rem', md: '1.3rem' },
  color: theme.palette.primary.light,
};

// Добавим глобальные стили для скрытия полосы прокрутки
const GlobalStyles = styled('style')({
  '& html::-webkit-scrollbar': {
    display: 'none', // для Chrome, Safari и Opera
  },
  '& html': {
    scrollbarWidth: 'none', // для Firefox
    msOverflowStyle: 'none', // для IE и Edge
    overflow: '-moz-scrollbars-none', // для старых версий Firefox
  }
});

// Добавляем стили для кнопки
const ScrollTopButton = styled('button')({
  position: 'fixed',
  bottom: '2rem',
  right: '2rem',
  width: '48px',
  height: '48px',
  borderRadius: '50%',
  background: 'rgba(157, 78, 221, 0.15)',
  backdropFilter: 'blur(10px)',
  border: '1px solid rgba(157, 78, 221, 0.2)',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'rgba(255, 255, 255, 0.8)',
  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
  transition: 'all 0.3s ease',
  zIndex: 1000,
  '&:hover': {
    background: 'rgba(157, 78, 221, 0.25)',
    transform: 'translateY(-2px)',
    boxShadow: '0 6px 16px rgba(157, 78, 221, 0.15)',
    color: 'rgba(255, 255, 255, 1)',
  }
});

// Добавим константу для цвета иконок - используем тот же розово-фиолетовый цвет
const iconColor = 'rgba(255, 105, 180, 1)'; // hot pink, как в градиенте обводки

const sectionHeaderStyles = {
    mb: 3,
    fontWeight: 600
};

const App: React.FC = () => {
  const [lang, setLang] = React.useState<'en' | 'ru' | 'th'>('en');
  const t: Translation = translations[lang];

  const techStack = [
    { name: 'JavaScript', Icon: DiJavascript1 },
    { name: 'Node.js', Icon: SiNodedotjs },
    { name: 'React', Icon: DiReact },
    { name: 'Redux', Icon: SiRedux },
    { name: 'HTML', Icon: FaHtml5 },
    { name: 'CSS', Icon: FaCss3Alt },
    { name: 'Webpack', Icon: SiWebpack },
    { name: 'TypeORM', Icon: DiDatabase },
    { name: 'PostgreSQL', Icon: DiPostgresql },
    { name: 'Express.js', Icon: SiExpress },
    { name: 'RabbitMQ', Icon: SiRabbitmq },
    { name: 'Nginx', Icon: SiNginx },
    { name: 'Redis', Icon: SiRedis },
    { name: 'DDD', Icon: () => <ArchitectureIcon sx={{ fill: '#ffffff' }} /> },
    { name: 'Kubernetes', Icon: SiKubernetes },
    { name: 'Grafana', Icon: SiGrafana },
    { name: 'Kibana', Icon: SiKibana },
    { name: 'CI/CD', Icon: SiGithubactions },
    { name: 'Docker', Icon: SiDocker },
    { name: 'Bash', Icon: VscTerminalBash },
    { name: 'Git', Icon: DiGit },
    { name: 'React Native', Icon: DiReact },
    { name: 'Xcode', Icon: SiXcode },
    { name: 'Fastlane', Icon: SiFastlane }
  ];

  const [showScrollTop, setShowScrollTop] = React.useState(false);

  React.useEffect(() => {
    const handleScroll = () => {
      // Показываем кнопку, когда прокрутка больше 400px
      setShowScrollTop(window.scrollY > 400);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  return (
    <>
      <GlobalStyles>{`
        html::-webkit-scrollbar {
          display: none;
        }
        html {
          scrollbar-width: none;
          -ms-overflow-style: none;
          overflow: -moz-scrollbars-none;
        }
      `}</GlobalStyles>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <GlowingBackground />
        <ParticlesBackground />
        <Header currentLang={lang} onChangeLang={setLang} />
        <Container maxWidth="lg">
          <Box sx={{ 
            py: { 
              xs: 2,
              sm: 3,
              md: 4 
            },
            mt: { 
              xs: '60px',
              sm: '70px',
              md: '80px' 
            }
          }}>
            <Grid container spacing={{ 
              xs: 2,
              sm: 3,
              md: 4
            }}>
              <Grid item xs={12}>
                <ContentCard sx={{ textAlign: 'center', mb: 4 }} noEffects>
                  <ProfileImage src="/assets/ava.jpeg" alt="Profile" />
                  <Typography 
                    variant="h3" 
                    sx={{ 
                      mt: 3, 
                      mb: 1,
                      lineHeight: lang === 'th' ? 1.4 : undefined,
                      fontWeight: 600
                    }}
                  >
                    {t.name}
                  </Typography>
                  <Typography variant="h5" sx={{ mb: 3, opacity: 0.9 }}>
                    {t.role}
                  </Typography>
                  
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    flexWrap: 'wrap',
                    gap: { xs: 1.5, sm: 2 },
                    mt: 3,
                    px: { xs: 2, sm: 0 },
                    width: '100%',
                    maxWidth: { xs: '100%', sm: 'auto' },
                    flexDirection: { xs: 'row', sm: 'row' },
                  }}>
                    <SocialButton 
                      href="https://t.me/b_u_b_n_o_v" 
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <TelegramIcon />
                      <span>Telegram</span>
                    </SocialButton>
                    <SocialButton 
                      href="https://line.me/ti/p/~b_u_b_n_o_v" 
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <LineIcon />
                      <span>LINE</span>
                    </SocialButton>
                    <SocialButton 
                      href="https://www.linkedin.com/in/sergey-bubnov-47b578353" 
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <LinkedInIcon />
                      <span>LinkedIn</span>
                    </SocialButton>
                    <SocialButton 
                      href="mailto:bubnovsa99@gmail.com" 
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <GmailIcon />
                      <span>Gmail</span>
                    </SocialButton>
                  </Box>
                </ContentCard>
              </Grid>

              <Grid item xs={12}>
                <ContentCard>
                  <Typography 
                    variant="h6" 
                    sx={sectionHeaderStyles}
                  >
                    {t.about.title}
                  </Typography>
                  <Typography 
                    variant="body2"
                    component="div"
                    sx={{ 
                      ...commonTextStyles,
                      '& p': {
                        ...commonTextStyles,
                        mb: 2,
                        '&:last-child': {
                          mb: 0
                        }
                      }
                    }}
                  >
                    {parse(t.about.text)}
                  </Typography>
                </ContentCard>
              </Grid>

              <Grid item xs={12}>
                <ContentCard>
                  <Typography 
                    variant="h6" 
                    sx={sectionHeaderStyles}
                  >
                    {t.development.title}
                  </Typography>
                  
                  <Box sx={{ mb: 4 }}>
                    <TechStackScroll>
                      <TechStackTrack>
                        {[...techStack, ...techStack].map((tech, index) => (
                          <TechItem key={`${tech.name}-${index}`}>
                            <Box sx={techIconStyle}>
                              <tech.Icon />
                            </Box>
                            <Typography 
                              variant="body2" 
                              sx={techTextStyle}
                            >
                              {tech.name}
                            </Typography>
                          </TechItem>
                        ))}
                      </TechStackTrack>
                    </TechStackScroll>
                  </Box>

                  <Grid container spacing={2} alignItems="stretch">
                    {t.development.items.map((exp, index) => (
                      <Grid item xs={12} md={6} key={index}>
                        <ExperienceCard>
                          <Typography variant="subtitle1" sx={{ 
                            fontWeight: 600, 
                            mb: 1,
                            flexGrow: 0
                          }}>
                            {exp.title}
                          </Typography>
                          <Typography variant="body2" sx={{ 
                            ...commonTextStyles,
                            flexGrow: 1
                          }}>
                            {exp.desc}
                          </Typography>
                        </ExperienceCard>
                      </Grid>
                    ))}
                  </Grid>
                </ContentCard>
              </Grid>

              <Grid item xs={12}>
                <ContentCard>
                  <Typography 
                    variant="h6" 
                    sx={sectionHeaderStyles}
                  >
                    {t.mentoring.title}
                  </Typography>
                  
                  <Box sx={{ mb: 4 }}>
                    {t.mentoring.intro.map((text, index) => (
                      <Typography 
                        key={index}
                        variant="body2" 
                        sx={{ 
                          ...commonTextStyles,
                          mb: 2,
                          '&:last-child': {
                            mb: 0
                          }
                        }}
                      >
                        {text}
                      </Typography>
                    ))}
                  </Box>

                  <Grid container spacing={{ xs: 2, md: 4 }}>
                    <Grid item xs={12} md={6}>
                      <Typography 
                        variant="subtitle1" 
                        sx={sectionTitleStyles}
                      >
                        {t.mentoring.roadmap.title}
                      </Typography>
                      <List sx={{ 
                        p: 0,
                        mb: { xs: 2, md: 0 }
                      }}>
                        {t.mentoring.roadmap.items.map((item, index) => (
                          <ListItem 
                            key={index}
                            sx={{
                              p: 0,
                              mb: 1,
                            }}
                          >
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                ...commonTextStyles,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                              }}
                            >
                              {(item.includes('Linux') || item.includes('операционной системой Linux') || item.includes('ระบบปฏิบัติการ Linux')) && 
                                <VscTerminalBash style={{ color: iconColor }} />}
                              {(item.includes('JavaScript') || item.includes('языком программирования JavaScript') || item.includes('ภาษาโปรแกรมมิ่ง JavaScript')) && 
                                <DiJavascript1 style={{ color: iconColor }} />}
                              {(item.includes('website') || item.includes('сайта') || item.includes('เว็บไซต์')) && 
                                <DiReact style={{ color: iconColor }} />}
                              {(item.includes('AI') || item.includes('искусственный интеллект') || item.includes('ปัญญาประดิษฐ์')) && 
                                <SmartToyIcon sx={{ color: iconColor }} />}
                              {(item.includes('workspace') || item.includes('рабочего пространства') || item.includes('พื้นที่ทำงาน')) && 
                                <SettingsIcon sx={{ color: iconColor }} />}
                              {(item.includes('programs') || item.includes('работы программ') || item.includes('โปรแกรม')) && 
                                !item.includes('JavaScript') &&
                                <CodeIcon sx={{ color: iconColor }} />}
                              {(item.includes('online') || item.includes('в сеть') || item.includes('ออนไลน์')) && 
                                <CloudUploadIcon sx={{ color: iconColor }} />}
                              {item}
                            </Typography>
                          </ListItem>
                        ))}
                      </List>
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <Typography 
                        variant="subtitle1" 
                        sx={sectionTitleStyles}
                      >
                        {t.mentoring.technologies.title}
                      </Typography>
                      <List sx={{ p: 0 }}>
                        {t.mentoring.technologies.items.map((item, index) => (
                          <ListItem 
                            key={index}
                            sx={{
                              p: 0,
                              mb: 1,
                            }}
                          >
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                ...commonTextStyles,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                              }}
                            >
                              {item.includes('Linux') && <VscTerminalBash style={{ color: iconColor }} />}
                              {item.includes('Git (') && <DiGit style={{ color: iconColor }} />}
                              {item.includes('GitHub') && <DiGithubBadge style={{ color: iconColor }} />}
                              {item.includes('JavaScript') && <DiJavascript1 style={{ color: iconColor }} />}
                              {item.includes('PostgresSQL') && <DiPostgresql style={{ color: iconColor }} />}
                              {item.includes('CSS') && <FaCss3Alt style={{ color: iconColor }} />}
                              {item.includes('HTML') && <FaHtml5 style={{ color: iconColor }} />}
                              {item.includes('Docker') && <SiDocker style={{ color: iconColor }} />}
                              {item.includes('Nginx') && <SiNginx style={{ color: iconColor }} />}
                              {item}
                            </Typography>
                          </ListItem>
                        ))}
                      </List>
                    </Grid>
                  </Grid>

                  <Box sx={{ mt: 4 }}>
                    {t.mentoring.conclusion.map((text, index) => (
                      <Typography 
                        key={index}
                        variant="body2" 
                        sx={{ 
                          ...commonTextStyles,
                          mb: 2,
                          '&:last-child': {
                            mb: 0
                          }
                        }}
                      >
                        {text}
                      </Typography>
                    ))}
                  </Box>
                </ContentCard>
              </Grid>
            </Grid>
          </Box>
        </Container>
        <Box 
          component="footer" 
          sx={{ 
            textAlign: 'center',
            py: { xs: 1, sm: 1.5 },
            mt: { xs: 0.5, sm: 1 },
            mb: { xs: 0.5, sm: 1 },
            opacity: 0.7,
            fontSize: '0.9rem',
          }}
        >
          <Typography variant="body2" sx={{ mb: 0.15 }}>
            © {new Date().getFullYear()} {t.name}
          </Typography>
          <Typography variant="body2">
            {t.footer.rights}
          </Typography>
        </Box>
        <Fade in={showScrollTop}>
          <ScrollTopButton
            onClick={scrollToTop}
            aria-label="scroll to top"
          >
            <KeyboardArrowUpIcon />
          </ScrollTopButton>
        </Fade>
      </ThemeProvider>
    </>
  );
};

export default App; 